/**
 * NotificacionConfigRepo sobre Supabase. Catálogo compartido de los 75 puntos
 * de notificación (estructural; se siembra en demo y operación).
 */

import type { NotificacionConfigRepo } from "../../repos/NotificacionConfigRepo";
import type { ConfiguracionNotificacion } from "@app-types/entities";
import type { GerenciaCodigo } from "@constants/gerencias";
import { HANDOVERS_CATALOGO } from "@features/notificaciones/handoversCatalog";
import { SupabaseBaseRepo } from "./SupabaseBaseRepo";

function extraerDestinatarios(pregunta: string): string {
  const m = /^(?:Notificar|Alertar|Avisar|Enviar|Recordar)\s+(?:a(?:l)?|al)\s+(.+?)\s+(?:cuando|si|que|entre|el)\b/i.exec(
    pregunta,
  );
  return m ? m[1]!.trim() : "";
}

export class SupabaseNotificacionConfigRepo
  extends SupabaseBaseRepo<ConfiguracionNotificacion>
  implements NotificacionConfigRepo
{
  constructor(tenantId: string) {
    super("notificacionConfig", tenantId);
  }

  async list(): Promise<ConfiguracionNotificacion[]> {
    return this.listAll();
  }

  async get(id: string): Promise<ConfiguracionNotificacion | null> {
    return this.getById(id);
  }

  async getByHandoverId(handoverId: string): Promise<ConfiguracionNotificacion | null> {
    return (await this.listAll()).find((c) => c.handoverId === handoverId) ?? null;
  }

  async update(
    id: string,
    data: Partial<ConfiguracionNotificacion>,
    expectedVersion: number,
  ): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`ConfiguracionNotificacion ${id} no encontrada`);
    const updated: ConfiguracionNotificacion = {
      ...current,
      ...data,
      // Auditoria de creacion INMUTABLE: ver BaseRepo.update.
      createdBy: current.createdBy,
      createdAt: current.createdAt,
      version: current.version + 1,
      updatedAt: new Date().toISOString(),
    };
    await this.updateRaw(updated, expectedVersion);
  }

  async ensureSeed(userEmail: string, gerenciaId?: GerenciaCodigo): Promise<number> {
    const existentes = await this.listAll();
    const yaPresentes = new Set(
      existentes
        .filter((c) => (gerenciaId ? c.gerenciaId === gerenciaId : true))
        .map((c) => c.handoverId),
    );
    const faltantes = HANDOVERS_CATALOGO.filter((h) => !yaPresentes.has(h.id));
    if (faltantes.length === 0) return 0;
    const now = new Date().toISOString();
    const nuevos: ConfiguracionNotificacion[] = faltantes.map((h) => ({
      id: crypto.randomUUID(),
      tenantId: this.tenantId,
      ...(gerenciaId ? { gerenciaId } : {}),
      handoverId: h.id,
      modulo: h.modulo,
      prioridad: h.prioridad,
      tipo: h.tipo,
      pregunta: h.pregunta,
      destinatarios: extraerDestinatarios(h.pregunta),
      habilitado: h.habilitadoDefault,
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: userEmail,
      updatedBy: userEmail,
    }));
    await this.insertManyRaw(nuevos);
    return nuevos.length;
  }
}
